/* Location breadcrumbs, contextual results return, and desktop filter defaults. */
(() => {
  'use strict';
  const prefix = 'pm-navigation:v1:';
  const lifetime = 30 * 60 * 1000;
  const read = key => { try { return JSON.parse(sessionStorage.getItem(prefix + key)); } catch { return null; } };
  const write = (key, value) => { try { sessionStorage.setItem(prefix + key, JSON.stringify(value)); } catch { /* Navigation still works when storage is unavailable. */ } };
  const safeURL = value => { try { const url = new URL(value, location.origin); return url.origin === location.origin && url.protocol === location.protocol ? url : null; } catch { return null; } };
  const isResults = url => url && (/\/(collections)(\/[^/]+)?\/?$/.test(url.pathname) || /\/search\/?$/.test(url.pathname));
  const productPath = url => url && /\/products\/[^/]+\/?$/.test(url.pathname) ? url.pathname.replace(/\/collections\/[^/]+(?=\/products\/)/, '') : null;
  const currentURL = () => location.pathname + location.search + location.hash;
  const resultsState = () => ({ url: currentURL(), scroll: window.scrollY, at: Date.now() });

  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    const url = safeURL(link.href);
    const path = productPath(url);
    if (path) write('product:' + path, isResults(safeURL(location.href)) ? resultsState() : null);
    if (link.matches('[data-pm-back-results]') && isResults(url)) {
      const state = read('product:' + productPath(safeURL(location.href)));
      if (state && safeURL(state.url)?.href === url.href) write('restore', state);
    }
  }, true);

  function returnLink() {
    const link = document.querySelector('[data-pm-back-results]');
    if (!link) return;
    let state = read('product:' + productPath(safeURL(location.href)));
    const referrer = safeURL(document.referrer);
    if (!state && isResults(referrer)) {
      state = { url: referrer.pathname + referrer.search + referrer.hash, scroll: 0, at: Date.now() };
      write('product:' + productPath(safeURL(location.href)), state);
    }
    const source = safeURL(state?.url);
    const navigation = performance.getEntriesByType('navigation')[0];
    const arrivedFromResults = referrer && isResults(referrer) && source && referrer.href === source.href;
    const freshClickWithoutReferrer = !document.referrer && state && Date.now() - state.at < 15000;
    if (!state || !isResults(source) || Date.now() - state.at > lifetime || (!arrivedFromResults && !freshClickWithoutReferrer && navigation?.type !== 'back_forward')) return;
    link.href = source.pathname + source.search + source.hash;
    link.hidden = false;
  }

  function restoreResults() {
    if (!isResults(safeURL(location.href))) return;
    const state = read('restore');
    if (!state || Date.now() - state.at > lifetime || safeURL(state.url)?.href !== location.href) return;
    write('restore', null);
    const y = Math.max(0, Number(state.scroll) || 0);
    const start = performance.now();
    let canceled = false;
    const cancel = () => { canceled = true; };
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(type => window.addEventListener(type, cancel, { once: true, passive: true }));
    function restore() {
      if (canceled) return;
      window.scrollTo({ top: y, behavior: 'instant' });
      if (Math.abs(window.scrollY - y) > 3 && performance.now() - start < 2500) requestAnimationFrame(restore);
    }
    requestAnimationFrame(() => requestAnimationFrame(restore));
  }

  function desktopFilters() {
    if (!document.body.classList.contains('template-collection')) return;
    const wide = window.matchMedia('(min-width:860px) and (hover:hover)');
    let initialized = new WeakSet();
    const groupSelector = '.productgrid--sidebar [data-filter-group]';
    const groupKey = group => group.dataset.pmFilterKey || group.querySelector('[data-filter-input]')?.dataset.name || group.querySelector('.collection-filters__filter-title')?.textContent.trim();
    const storageKey = 'filters:' + location.pathname;
    const choices = read(storageKey) || {};
    function setOpen(group, open) {
      const button = group.querySelector('[data-filter-group-trigger]');
      const content = group.querySelector('[data-accordion-content]');
      if (!button || !content) return;
      group.setAttribute('data-pm-filter-open', String(open));
      button.setAttribute('aria-expanded', String(open));
      button.classList.toggle('productgrid--sidebar-button-active', open);
      content.setAttribute('data-accordion-state', open ? 'open' : 'closed');
      content.removeAttribute('data-accordion-animation');
      content.style.height = open ? 'auto' : '0px';
      content.style.maxHeight = open ? 'none' : '';
    }
    function apply() {
      if (!wide.matches) return;
      const groups = [...document.querySelectorAll(groupSelector)].filter(group => !group.hidden && getComputedStyle(group).display !== 'none');
      const primary = groups.filter(group => /series|denomination/i.test(groupKey(group) || ''));
      groups.forEach(group => { if (primary.length < 2 && !primary.includes(group)) primary.push(group); });
      groups.forEach(group => {
        if (initialized.has(group)) return;
        initialized.add(group);
        const key = groupKey(group);
        const active = Boolean(group.querySelector('[data-filter-active], [data-filter-open="true"]'));
        setOpen(group, active || (Object.hasOwn(choices, key) ? choices[key] : primary.includes(group)));
        const list = group.querySelector('[data-filter-group-list]');
        const rows = list ? [...list.children].filter(row => !row.hidden) : [];
        if (rows.length > 11) {
          let expanded = false;
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'pm-filter-more';
          const labels = document.querySelector('[data-pm-navigation-labels]');
          let text;
          try { text = JSON.parse(labels.textContent); } catch { return; }
          const refresh = () => {
            rows.slice(10).forEach(row => { row.classList.add('pm-filter-truncated'); row.hidden = !expanded && !row.querySelector('[data-filter-active]'); });
            more.textContent = expanded ? text.less : text.more;
            more.setAttribute('aria-expanded', String(expanded));
          };
          more.addEventListener('click', () => { expanded = !expanded; refresh(); });
          const moreRow = document.createElement('li');
          moreRow.append(more);
          list.append(moreRow);
          refresh();
        }
      });
    }
    document.addEventListener('click', event => {
      const button = event.target.closest?.('.productgrid--sidebar [data-filter-group-trigger]');
      if (!button || !wide.matches) return;
      // Own the desktop toggle so late native initialization cannot reverse its state.
      event.preventDefault();
      event.stopImmediatePropagation();
      const group = button.closest('[data-filter-group]');
      const open = group.getAttribute('data-pm-filter-open') !== 'true';
      setOpen(group, open);
      choices[groupKey(group)] = open;
      write(storageKey, choices);
    }, true);
    new MutationObserver(apply).observe(document.querySelector('[data-productgrid-outer]') || document.querySelector('#site-main'), { childList: true, subtree: true });
    wide.addEventListener('change', () => {
      initialized = new WeakSet();
      if (wide.matches) apply();
      else document.querySelectorAll(groupSelector).forEach(group => setOpen(group, Boolean(group.querySelector('[data-filter-active], [data-filter-open="true"]'))));
    });
    apply();
  }
  function boot() { returnLink(); restoreResults(); desktopFilters(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  window.addEventListener('pageshow', returnLink);
})();
